import { createApi } from '@reduxjs/toolkit/query/react';
import {
  BillingBalanceResponse,
  BillingCreditPacksResponse,
  BillingHistoryParams,
  BillingHistoryResponse,
  BillingInfo,
  GetStripeInvoicesParams,
  GetStripeInvoicesResponse,
  GetUserBillingResponse,
  GetWireInvoicesParams,
  GetWireInvoicesResponse,
  PatchBillingAutodebitResponse,
  PatchBillingInfoBodyRequest,
  PatchBillingInfoResponseData,
  PostTopUpSubscriptionResponse,
  PostWireInvoiceRequest,
  PostWireInvoiceResponse,
  WireInvoicePreviewResponse,
  WireInvoiceResource,
} from './models';
import { BILLING_ENDPOINTS } from './endpoints';
import { baseQueryWithReauth } from '../baseQueryWithReauth';

const billing = `billing`;

export const billingApi = createApi({
  reducerPath: billing,
  baseQuery: baseQueryWithReauth,
  tagTypes: [billing],
  endpoints: (build) => ({
    // BILLING

    getUserBilling: build.query<
      GetUserBillingResponse,
      {
        last?: string;
        dateFrom?: string;
        dateTo?: string;
      }
    >({
      query: ({ last, dateFrom, dateTo }) => {
        const params = new URLSearchParams();

        const lastQueryString = last ? `last=${last}` : '';

        if (dateFrom) {
          params.append('filters[date][from]', `${dateFrom}`);
        }

        if (dateTo) {
          params.append('filters[date][to]', `${dateTo}`);
        }

        const filterQueryString = params.toString();

        const queryString = [lastQueryString, filterQueryString].filter(Boolean).join('&');
        return {
          url: `${BILLING_ENDPOINTS.root}${queryString.length ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [{ type: billing, id: 'OVERVIEW' }],
    }),
    patchBillingInfo: build.mutation<PatchBillingInfoResponseData, PatchBillingInfoBodyRequest>({
      query: (body) => ({
        body,
        method: 'PATCH',
        url: BILLING_ENDPOINTS.root,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [{ type: billing, id: 'OVERVIEW' }],
    }),
    patchBillingAutodebit: build.mutation<
      PatchBillingAutodebitResponse,
      {
        amount: number;
        autodebit: boolean;
      }
    >({
      query: (body) => ({
        body,
        method: 'PATCH',
        url: BILLING_ENDPOINTS.autodebit,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [{ type: billing, id: 'OVERVIEW' }],
    }),
    postBillingTopUp: build.mutation<
      unknown,
      {
        amount: number;
        payment_method_id?: string;
      }
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: BILLING_ENDPOINTS.topUp,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [
        { type: billing, id: 'OVERVIEW' },
        { type: billing, id: 'STRIPE_LIST' },
      ],
    }),
    postBillingTopUpSubscription: build.mutation<
      PostTopUpSubscriptionResponse,
      {
        subscription_id?: number;
        type?: number;
      }
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: BILLING_ENDPOINTS.topUpSubscription,
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [
        { type: billing, id: 'OVERVIEW' },
        { type: billing, id: 'STRIPE_LIST' },
      ],
    }),
    getBillingInfo: build.query<{ data: BillingInfo }, void>({
      query: () => ({
        url: BILLING_ENDPOINTS.info,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      providesTags: [],
    }),
    getCreditPacks: build.query<BillingCreditPacksResponse, void>({
      query: () => ({
        url: BILLING_ENDPOINTS.creditsPacks,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: [{ type: billing, id: 'CREDIT_PACKS' }],
    }),
    postBuyCredits: build.mutation<
      unknown,
      {
        /** the size of the pack bought — one of the packs on sale */
        credits: number;
      }
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: BILLING_ENDPOINTS.creditsBuy,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [
        { type: billing, id: 'OVERVIEW' },
        { type: billing, id: 'STRIPE_LIST' },
      ],
    }),
    getBillingBalance: build.query<BillingBalanceResponse, void>({
      query: () => ({
        url: BILLING_ENDPOINTS.balance,
        headers: {
          Accept: 'application/json',
        },
      }),
    }),

    getBillingHistory: build.query<BillingHistoryResponse, BillingHistoryParams>({
      query: ({ page, dateFrom, dateTo, paymentMethods }) => {
        const params = new URLSearchParams();

        if (page) {
          params.append('page', page.toString());
        }

        if (dateFrom) {
          params.append('filters[date][from]', dateFrom);
        }

        if (dateTo) {
          params.append('filters[date][to]', dateTo);
        }

        if (paymentMethods && paymentMethods.length > 0) {
          paymentMethods.forEach((method) => {
            params.append('filters[payment_methods][]', method.toString());
          });
        }

        const queryString = params.toString();
        return {
          url: `${BILLING_ENDPOINTS.history}${queryString.length ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: billing, id: 'HISTORY' }],
    }),

    // Wire Invoices
    getWireInvoices: build.query<GetWireInvoicesResponse, GetWireInvoicesParams>({
      query: ({ statuses, dateFrom, dateTo }) => {
        const params = new URLSearchParams();

        if (statuses && statuses.length > 0) {
          statuses.forEach((status) => {
            params.append('filters[statuses][]', status.toString());
          });
        }

        if (dateFrom) {
          params.append('filters[date][from]', dateFrom);
        }

        if (dateTo) {
          params.append('filters[date][to]', dateTo);
        }

        const queryString = params.toString();
        return {
          url: `${BILLING_ENDPOINTS.wireInvoices}${queryString.length ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: billing, id: 'WIRE_LIST' }],
    }),

    // Stripe (card) invoices — cursor pagination via `last` (id of the last loaded invoice)
    getStripeInvoices: build.query<GetStripeInvoicesResponse, GetStripeInvoicesParams>({
      query: ({ last, dateFrom, dateTo }) => {
        const params = new URLSearchParams();

        if (last) {
          params.append('last', last);
        }

        if (dateFrom) {
          params.append('filters[date][from]', dateFrom);
        }

        if (dateTo) {
          params.append('filters[date][to]', dateTo);
        }

        const queryString = params.toString();
        return {
          url: `${BILLING_ENDPOINTS.stripeInvoices}${queryString.length ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: billing, id: 'STRIPE_LIST' }],
    }),

    getWireInvoiceById: build.query<{ data: WireInvoiceResource }, number>({
      query: (id) => ({
        url: `${BILLING_ENDPOINTS.wireInvoices}/${id}`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, id) => [{ type: billing, id: `wire_${id}` }],
    }),

    postWireInvoicePreview: build.mutation<WireInvoicePreviewResponse, PostWireInvoiceRequest>({
      query: (body) => ({
        body,
        method: 'POST',
        url: BILLING_ENDPOINTS.wireInvoicesPreview,
        headers: {
          Accept: 'application/json',
        },
      }),
    }),

    postWireInvoice: build.mutation<PostWireInvoiceResponse, PostWireInvoiceRequest>({
      query: (body) => ({
        body,
        method: 'POST',
        url: BILLING_ENDPOINTS.wireInvoices,
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [
        { type: billing, id: 'WIRE_LIST' },
        { type: billing, id: 'OVERVIEW' },
      ],
    }),
  }),
});

export const {
  useGetUserBillingQuery,
  usePatchBillingAutodebitMutation,
  usePatchBillingInfoMutation,
  useGetBillingInfoQuery,
  useGetCreditPacksQuery,
  usePostBuyCreditsMutation,
  usePostBillingTopUpMutation,
  usePostBillingTopUpSubscriptionMutation,
  useGetBillingBalanceQuery,
  useLazyGetBillingBalanceQuery,
  useGetBillingHistoryQuery,
  // Stripe Invoices
  useGetStripeInvoicesQuery,
  // Wire Invoices
  useGetWireInvoicesQuery,
  useGetWireInvoiceByIdQuery,
  usePostWireInvoicePreviewMutation,
  usePostWireInvoiceMutation,
} = billingApi;
